package dev.lingvo.site.components.review

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderTop
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ReviewAction
import dev.lingvo.shared.ReviewBatchRequest
import dev.lingvo.shared.ReviewBatchResult
import dev.lingvo.shared.ReviewItemDto
import dev.lingvo.shared.SourceReviewBatchRequest
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCheckCheckIcon
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tr
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Batch approval, and the gate that is the only reason it may exist.
 *
 * The review lane's rule is that somebody read the string. It used to be protected by refusing bulk
 * approval outright, which also made a reviewer who had genuinely read a screenful press the same
 * button forty times. The absolute is now a gate, and every part of it is load-bearing:
 *
 *  1. **Exact scope.** Only the QA-clean strings on the screenful in front of the reviewer, named
 *     one by one in the request at the versions that were read. Never "everything matching a
 *     filter", and there is no select-all grid from which to build one.
 *  2. **Server-side QA re-evaluation.** The dry run is the same route, the same gate and the same
 *     per-item answers with nothing written, so the preview is the server's verdict rather than a
 *     second opinion computed in the browser.
 *  3. **A preview with a count**, listing every key and who submitted it, saying which ones the
 *     server has already refused and why.
 *  4. **A partial-failure receipt.** The response is per item: one blocked string does not refuse
 *     the other hundred, and everything that was not decided says why.
 *  5. **The same per-item guarantees as the single route** — approve scope, reviewer authority,
 *     `PENDING`, the expected version, origin preserved, one audit row each.
 *
 * The control is absent, not disabled, for anybody the server would refuse, and the claim captured
 * when it is pressed is checked again before the write and before the receipt is published.
 */
@Composable
internal fun ReviewBatchApprove(
    slug: String,
    locale: String,
    eligible: List<ReviewItemDto>,
    canApprove: Boolean,
    currentlyCanApprove: () -> Boolean,
    onRefused: (String) -> Unit,
    onFinished: (ReviewBatchCounts) -> Unit,
    guardedMode: Boolean = false,
) {
    val claim = ReviewBatchClaim.capture(slug, locale, eligible, canApprove, guardedMode) ?: return

    val scope = rememberCoroutineScope()
    var preview by remember(claim.fingerprint) { mutableStateOf<ReviewBatchPreview?>(null) }
    var busy by remember(claim.fingerprint) { mutableStateOf(false) }
    var running by remember(claim.fingerprint) { mutableStateOf(false) }
    var failure by remember(claim.fingerprint) { mutableStateOf<String?>(null) }

    LButton(
        label = tPlural(
            "review.review_batch_approve.approve_the_count_qa_clean_string",
            claim.covered.size,
            "Approve the {count} QA-clean string on this screen",
            "Approve the {count} QA-clean strings on this screen",
        ),
        onClick = {
            scope.launch {
                if (!claim.remainsHeld(currentlyCanApprove())) return@launch
                busy = true
                failure = null
                try {
                    val dry = if (claim.guarded) ApiClient.reviewDecideBatchForSource(slug, locale, claim.sourceRequest(dryRun = true))
                    else ApiClient.reviewDecideBatch(slug, locale, claim.request(dryRun = true))
                    if (!claim.remainsHeld(currentlyCanApprove())) return@launch
                    val previewed = claim.previewOf(dry)
                    // Nothing to confirm is not a dialog. The server has already refused every
                    // string, and the honest answer is the receipt saying so — with the queue
                    // reloaded underneath it, because being this out of date is why it refused.
                    if (previewed.counts.decided == 0) onFinished(previewed.counts) else preview = previewed
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (refusal: ApiException) {
                    // Nothing has opened yet, so this belongs in the page's own banner rather than
                    // in a second alert wedged into the header row beside the button.
                    if (claim.remainsHeld(currentlyCanApprove())) onRefused(refusal.message)
                } finally {
                    busy = false
                }
            }
        },
        // One primary action per screen, and on this screen it is the decision about the string the
        // reviewer is reading. The accelerator sits beside that, never above it.
        variant = LButtonVariant.SECONDARY,
        size = LButtonSize.SM,
        enabled = !busy,
        leadingIcon = { LCheckCheckIcon() },
        loading = busy,
    )

    val shown = preview
    LConfirmDialog(
        open = shown != null,
        title = tPlural(
            "review.review_batch_approve.approve_count_qa_clean_string",
            shown?.counts?.decided ?: 0,
            "Approve {count} QA-clean string",
            "Approve {count} QA-clean strings",
        ),
        confirmLabel = tPlural(
            "review.review_batch_approve.approve_count_string",
            shown?.counts?.decided ?: 0,
            "Approve {count} string",
            "Approve {count} strings",
        ),
        onConfirm = {
            val previewed = shown
            scope.launch {
                if (previewed == null || previewed.decidable.isEmpty()) return@launch
                if (!claim.remainsHeld(currentlyCanApprove())) return@launch
                running = true
                failure = null
                try {
                    val response = if (claim.guarded) ApiClient.reviewDecideBatchForSource(
                        slug, locale, SourceReviewBatchRequest(previewed.sourceDecidable, ReviewAction.APPROVE),
                    ) else ApiClient.reviewDecideBatch(
                        slug,
                        locale,
                        ReviewBatchRequest(items = previewed.decidable, action = ReviewAction.APPROVE),
                    )
                    // The last check, and the one that matters most: a receipt published after the
                    // authority changed would credit this reviewer with somebody else's run.
                    if (!claim.remainsHeld(currentlyCanApprove())) return@launch
                    preview = null
                    // The whole batch, not the request: the strings the preview refused were never
                    // sent, so the run's response cannot account for them and a receipt built from
                    // it alone would say everything went through.
                    val submittedIds = previewed.decidable.mapTo(mutableSetOf()) { it.keyId }
                    onFinished(reviewCommittedBatchCounts(response, claim.covered.filter { it.keyId in submittedIds }) + previewed.skipped)
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (refusal: ApiException) {
                    if (claim.remainsHeld(currentlyCanApprove())) failure = refusal.message
                } finally {
                    running = false
                }
            }
        },
        onCancel = { if (!running) preview = null },
        tone = LConfirmTone.NEUTRAL,
        busy = running,
        busyLabel = t(
            "review.review_batch_approve.approving_each_string_on_the_server",
            "Approving each string on the server.",
        ),
        // A write that failed with the dialog open is answered where the reader already is.
        error = failure,
    ) {
        shown?.let { ReviewBatchPreviewBody(it, claim.capped) }
    }
}

/** What the server said it would do, per item, before anything was written. */
@Composable
private fun ReviewBatchPreviewBody(preview: ReviewBatchPreview, capped: Boolean) {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        LMuted(
            t(
                "review.review_batch_approve.each_string_is_checked_again_on",
                "Each string is checked again on the server; anything with a QA error or edited " +
                    "since you loaded it is skipped.",
            ),
        )
        if (capped) {
            LMuted(
                t(
                    "review.review_batch_approve.a_batch_carries_at_most_200",
                    "A batch carries at most 200 strings, so this one covers the first 200 on this screen.",
                ),
            )
        }
        // **Every key, not the first six and half of the seventh.** At 240px with a "Will be
        // approved" line under every row, a twelve-string batch showed six rows and a seventh cut
        // through the middle by the footer rule — and macOS draws no scrollbar to say the rest was
        // there, so the panel read as truncated rather than scrollable. The repeated line is gone
        // (below) and the cap now clears twelve single-line rows, so the common batch needs no
        // scrolling at all. The hairlines above and below say where the region ends when it does
        // scroll, which is what a clipped half-row was doing badly.
        Column(
            Modifier
                .fillMaxWidth()
                .gap(Space.XS)
                .maxHeight(320.px)
                .overflow(Overflow.Auto)
                .borderTop(1.px, LineStyle.Solid, palette.border)
                .borderBottom(1.px, LineStyle.Solid, palette.border)
                .padding(topBottom = Space.XS)
                .attr("data-review-batch-preview", preview.rows.size.toString()),
        ) {
            preview.rows.forEach { row ->
                Column(Modifier.fillMaxWidth()) {
                    Row(Modifier.gap(Space.XS), verticalAlignment = Alignment.CenterVertically) {
                        Span(
                            Modifier.monoFont().fontSize(TypeScale.Small).fontWeight(600).toAttrs(),
                        ) {
                            Text(row.keyName)
                        }
                        row.submittedBy?.let { who ->
                            Span(
                                Modifier.fontSize(TypeScale.Small).color(palette.textSecondary).toAttrs(),
                            ) {
                                Text(who)
                            }
                        }
                    }
                    // Only the skips carry a reason. "Will be approved" under all twelve rows was
                    // the dialog's own premise repeated twelve times — it is what the title and the
                    // confirm button both say — and it was what filled the scroll box that then hid
                    // half the list. What a reader needs per row is the exception.
                    reviewBatchRowOutcome(row.result)?.let { outcome ->
                        Span(
                            Modifier
                                .fontSize(TypeScale.Small)
                                .color(palette.warning)
                                .toAttrs { attr("data-review-batch-row", row.result.name.lowercase()) },
                        ) {
                            Text(outcome)
                        }
                    }
                }
            }
        }
    }
}

/**
 * The receipt, as whole sentences rather than a line assembled from fragments.
 *
 * Each clause is a counted message in its own right, drawn as its own element: a reader's language
 * decides how "three were skipped because QA errors block approval" is phrased, and a middot glued
 * on in Kotlin would have decided that here instead.
 */
@Composable
internal fun ReviewBatchReceipt(counts: ReviewBatchCounts, onDismiss: () -> Unit) {
    // One live region around the whole receipt, rather than one around the headline and none around
    // the reasons. A reader who is told "1 string approved" and never told that another was skipped
    // has been given the half of the receipt that needs no explaining.
    Column(
        Modifier
            .fillMaxWidth()
            .gap(Space.XS)
            .attr("role", "status")
            .attr("aria-live", "polite")
            .attr("data-review-receipt", "true"),
    ) {
        LAlert(
            title = if (counts.unconfirmed) t(
                "review.review_batch_approve.batch_results_are_unconfirmed",
                "Batch results are unconfirmed",
            ) else tPlural(
                "review.review_batch_approve.count_string_approved",
                counts.decided,
                "{count} string approved",
                "{count} strings approved",
            ),
            description = when {
                counts.unconfirmed -> t(
                    "review.review_batch_approve.reload_before_deciding_again",
                    "Some results could not be confirmed. Reload the queue before deciding again; the batch will not be retried.",
                )
                counts.decided == 0 -> t(
                    "review.review_batch_approve.nothing_was_written_the_server_refused",
                    "Nothing was written. The server refused every string in the batch, and the " +
                        "queue below has been reloaded.",
                )
                counts.skipped == 0 -> t(
                    "review.review_batch_approve.every_string_in_the_batch_went",
                    "Every string in the batch went through.",
                )
                else -> null
            },
            variant = if (counts.unconfirmed) LAlertVariant.WARNING else if (counts.skipped == 0) LAlertVariant.SUCCESS else LAlertVariant.INFO,
            // The region above announces this; a second one nested inside it says everything twice.
            live = false,
            onDismiss = onDismiss,
        )
        if (counts.blocked > 0) {
            LMuted(
                tPlural(
                    "review.review_batch_approve.count_string_was_skipped_because_qa",
                    counts.blocked,
                    "{count} string was skipped because QA errors block approval.",
                    "{count} strings were skipped because QA errors block approval.",
                ),
            )
        }
        if (counts.alreadyReviewed > 0) {
            LMuted(
                tPlural(
                    "review.review_batch_approve.count_string_had_already_been_reviewed",
                    counts.alreadyReviewed,
                    "{count} string had already been reviewed by somebody else.",
                    "{count} strings had already been reviewed by somebody else.",
                ),
            )
        }
        if (counts.stale > 0) {
            LMuted(
                tPlural(
                    "review.review_batch_approve.count_string_changed_while_you_were",
                    counts.stale,
                    "{count} string changed while you were reading it and was left alone.",
                    "{count} strings changed while you were reading them and were left alone.",
                ),
            )
        }
    }
}

/**
 * The reason a previewed row will *not* be approved, explained before the press rather than after.
 *
 * Null for [ReviewBatchResult.DECIDED]: that is what the whole dialog is about, said by its title
 * and by its confirm button, and saying it again under every key was twelve repetitions of the
 * premise crowding out the six keys they hid.
 */
private fun reviewBatchRowOutcome(result: ReviewBatchResult): String? = when (result) {
    ReviewBatchResult.DECIDED -> null
    ReviewBatchResult.BLOCKED -> tr(
        "review.review_batch_approve.skipped_qa_errors_block_approval",
        "Skipped — QA errors block approval",
    )
    ReviewBatchResult.ALREADY_REVIEWED -> tr(
        "review.review_batch_approve.skipped_already_reviewed",
        "Skipped — already reviewed",
    )
    ReviewBatchResult.STALE -> tr(
        "review.review_batch_approve.skipped_changed_since_you_loaded_it",
        "Skipped — changed since you loaded it",
    )
}
